/**
 * Вкладка «Обзор» окна товара: где товар присутствует (площадка × кабинет),
 * данные категории (то, что карточка наследует: состав, срок, страна, вес,
 * габариты, сертификаты) и три последних события с переходом в «Историю».
 */

import React from 'react'
import { ProductPresencePanel } from '../ProductPresencePanel'
import { ActivityTimeline } from '../../../../components/ui/ActivityTimeline'
import type { PresenceData } from '../../presence'
import type { ProductPresenceEntry, ProductV2 } from '../../types'
import { formatDate } from '../../lib'
import { historyToEvents, type HistoryData } from '../ProductHistoryPanel'
import { NoData, Row, SectionTitle } from './parts'

interface OverviewTabProps {
  product: ProductV2
  presence: PresenceData
  history: HistoryData
  onOpenEntry?: (entry: ProductPresenceEntry) => void
  onShowHistory: () => void
}

const dims = (a?: number | null, b?: number | null, c?: number | null): string | null =>
  [a, b, c].every((value) => value !== null && value !== undefined) ? `${a} × ${b} × ${c} мм` : null

const grams = (value?: number | null) => (value === null || value === undefined ? null : `${value} г`)

/** «поле» по-русски: 1 поле, 2 поля, 5 полей. */
const pluralFields = (n: number): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'поле'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'поля'
  return 'полей'
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ product, presence, history, onOpenEntry, onShowHistory }) => {
  const category = product.category
  const recent = history.items ? historyToEvents(history.items.slice(0, 3)) : []
  const certificate =
    [
      category?.certificate_number && `№ ${category.certificate_number}`,
      category?.declaration_number && `декларация ${category.declaration_number}`,
      category?.cert_expiry_date && `до ${formatDate(category.cert_expiry_date)}`,
    ]
      .filter(Boolean)
      .join(' · ') || null

  const rows: { label: string; value: string | null; why?: string }[] = [
    { label: 'Состав', value: category?.composition || product.composition || null, why: 'у категории состав не задан' },
    { label: 'Срок годности', value: category?.shelf_life || null },
    { label: 'Страна происхождения', value: category?.country_of_origin || null },
    {
      label: 'Вес нетто / брутто',
      value:
        category?.weight_net != null || category?.weight_gross != null
          ? `${grams(category?.weight_net) ?? '—'} / ${grams(category?.weight_gross) ?? '—'}`
          : null,
    },
    { label: 'Габариты изделия', value: dims(category?.item_height, category?.item_width, category?.item_depth) },
    { label: 'Габариты упаковки', value: dims(category?.package_length, category?.package_width, category?.package_height) },
    { label: 'Сертификат', value: certificate },
  ]
  const filledRows = rows.filter((row) => row.value)
  const emptyRows = rows.filter((row) => !row.value)

  return (
    <div className="space-y-5">
      <section>
        <SectionTitle>Площадки и кабинеты</SectionTitle>
        <ProductPresencePanel article={product.article} data={presence} onOpenEntry={onOpenEntry} />
      </section>

      <section data-testid="category-data">
        <SectionTitle source="из категории">Данные категории</SectionTitle>
        {filledRows.length === 0 ? (
          <p className="py-1.5 text-sm text-gray-500 dark:text-zinc-400">Данных категории в ПИМ нет</p>
        ) : (
          <>
            {filledRows.map((row) => (
              <Row key={row.label} label={row.label}>
                {row.value}
              </Row>
            ))}
            {emptyRows.length > 0 && (
              <details className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                <summary className="cursor-pointer select-none hover:text-gray-800 dark:hover:text-zinc-100">
                  Ещё {emptyRows.length} {pluralFields(emptyRows.length)} без данных
                </summary>
                {emptyRows.map((row) => (
                  <Row key={row.label} label={row.label}>
                    <NoData why={row.why} />
                  </Row>
                ))}
              </details>
            )}
          </>
        )}
      </section>

      <section data-testid="recent-events">
        <div className="mb-1 flex items-center justify-between">
          <SectionTitle>Последние события</SectionTitle>
          <button
            type="button"
            onClick={onShowHistory}
            className="text-xs text-indigo-600 hover:underline dark:text-indigo-400"
          >
            Вся история
          </button>
        </div>
        {history.error ? (
          <p className="text-sm text-red-600 dark:text-red-400">{history.error}</p>
        ) : history.items === null ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">Загрузка истории</p>
        ) : (
          <ActivityTimeline events={recent} emptyState="По этому товару событий пока нет" />
        )}
      </section>
    </div>
  )
}
